import { AlbumList, Header, LoadMore } from '@/components/pages/albums'
import React, { FormEvent, useEffect, useState } from 'react'
import { clientGetAlbumsWithCount, clientGetTracks } from '@/client'
import {
	getAlbumsWithCount,
	getGenres,
	getTracks,
} from '@/pages/api/musicapp/_services/musicService'

import { GetServerSideProps } from 'next/types'
import Head from 'next/head'
import formatTitleByLocale from '@/utils/formatting/formatTitleByLocale'
import { getTopTracksFromAlbums } from '@/utils/getTopTracksFromAlbums'
import { sendAnalytics } from '@audio-network/analytics'
import { usePlayerApi } from '@audio-network/player'
import { withGlobalTranslations } from '@/utils/i18n/withGlobalTranslations'

export default function Albums({
	locale,
	albumsData,
	genres,
	canLoadMore,
	initialAlbumTracks,
}: {
	locale: string
	albumsData: AlbumResponse
	genres: Genre[]
	canLoadMore: boolean
	initialAlbumTracks: Track[]
}): JSX.Element {
	const { initialiseQueue } = usePlayerApi()

	const [canLoadMoreAlbums, setCanLoadMoreAlbums] = useState(canLoadMore)
	const [loading, setLoading] = useState(false)
	const [page, setPage] = useState(1)
	const [pageQueue, setPageQueue] = useState(initialAlbumTracks)
	const [albumList, setAlbumList] = useState(albumsData.albums)
	const [genreId, setGenreId] = useState<number>(null)
	const [albumCount, setAlbumCount] = useState(albumsData.albumCount)

	useEffect(() => {
		sendAnalytics({
			noun: 'All Albums Page',
			pastTenseVerb: 'Viewed',
		})
	}, [])

	useEffect(() => {
		initialiseQueue(pageQueue)
	}, [pageQueue, initialiseQueue])

	useEffect(() => {
		setCanLoadMoreAlbums(albumList.length < albumCount)
	}, [albumCount, albumList.length])

	const handleLoadMore = async (): Promise<void> => {
		const nextPage = page + 1

		sendAnalytics({
			noun: 'Albums Paging',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Take: 24, // hard coded limit
				Page: nextPage,
			},
		})

		setLoading(true)

		const nextAlbumsData: AlbumResponse = await clientGetAlbumsWithCount(
			page + 1,
			locale,
			genreId
		)
		if (typeof nextAlbumsData !== 'undefined') {
			const nextAlbumTrackIds = getTopTracksFromAlbums(nextAlbumsData.albums)
			const nextPageAlbumPreviewTracks = await clientGetTracks(
				nextAlbumTrackIds
			)
			setAlbumList([...albumList, ...nextAlbumsData?.albums])
			setPageQueue([...pageQueue, ...nextPageAlbumPreviewTracks])
			setPage(nextPage)
		}

		setLoading(false)
	}

	const handleSelect = async (event: FormEvent): Promise<void> => {
		const { value } = event.target as HTMLSelectElement

		const genreId = value !== 'all' && parseInt(value, 10)

		setGenreId(genreId)

		const genreName =
			genreId && genres.find((genre) => genre.id === genreId).name

		sendAnalytics({
			noun: 'Albums Filter',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Name: genreName ?? 'All Albums',
				GenreId: genreId,
			},
		})

		// TODO: Address what "loading" really means here. Do we need skeletons?
		setLoading(true)

		const filteredAlbumsData: AlbumResponse = await clientGetAlbumsWithCount(
			1,
			locale,
			genreId
		)

		if (typeof filteredAlbumsData !== 'undefined') {
			const filteredAlbumTrackIds = getTopTracksFromAlbums(
				filteredAlbumsData.albums
			)
			const filteredAlbumPreviewTracks = await clientGetTracks(
				filteredAlbumTrackIds
			)
			setAlbumList(filteredAlbumsData?.albums)
			setAlbumCount(filteredAlbumsData.albumCount)
			setPageQueue([...filteredAlbumPreviewTracks])
			setPage(1)

			setLoading(false)
		}
	}

	return (
		<>
			<Head>
				<title>{formatTitleByLocale(locale)}</title>
			</Head>

			<Header />

			<AlbumList
				albums={albumList}
				albumCount={albumCount}
				genres={genres}
				onSelect={handleSelect}
				locale={locale}
				albumPreviewTracks={pageQueue}
			/>

			{canLoadMoreAlbums && (
				<LoadMore loading={loading} onLoadMore={handleLoadMore} />
			)}
		</>
	)
}

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
	const albumsData = await getAlbumsWithCount(1, locale)

	const requiredGenresById = [
		1, // Blues
		3, // Christmas
		4, // Classical
		5, // Comedy
		6, // Country
		8, // Dance & Electronic
		9, // Documentary
		10, // Drama
		11, // Folk
		12, // Hip Hop/R&B
		13, // Indie
		14, // International
		15, // Jazz
		16, // Latin
		17, // Lifestyle
		18, // Musical Effects
		19, // Orchestral
		20, // Pop
		22, // Rock
	]

	const genres = await getGenres(locale).then((response) => {
		return response.filter((genre) => requiredGenresById.includes(genre.id))
	})

	const canLoadMore = albumsData.albums.length < albumsData.albumCount

	const initialAlbumTrackIds: number[] = getTopTracksFromAlbums(
		albumsData.albums
	)

	const initialAlbumTracks = await getTracks(initialAlbumTrackIds.map(String))

	return {
		props: {
			...(await withGlobalTranslations(locale, ['albums'])),
			locale,
			albumsData,
			genres,
			canLoadMore,
			initialAlbumTracks,
		},
	}
}
